import { Box, Card, CardContent, Divider, Typography } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { LoaderV1 } from '..';
import { QRCodeSVG } from 'qrcode.react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { getBookingStatus } from 'services/bookingService';
import { AxiosError } from 'axios';
import {
  initialUserSearchState,
  useUserSearchContext,
} from 'context/UserSearchContext';
import { useGetBookingDetails } from 'hooks/useBooking';

interface PayAmountWithQRCodeString {
  link: string | undefined;
  days: number;
  price: number | string;
}

export const PayAmountWithQRCode: React.FC<PayAmountWithQRCodeString> = ({
  link,
  days,
  price,
}) => {
  const [searchParams] = useSearchParams();
  const locationCode = searchParams.get('locationCode');
  const {
    bookingSearchData,
    setBookingSearchData,
    loading: bookingSearchDataLoading,
  } = useUserSearchContext();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [timeLeft, setTimeLeft] = useState(180);
  const timerRef = useRef<number | null>(null);
  const [timerId, setTimerId] = useState<number | null>(null);
  const navigate = useNavigate();

  const { bookingId } = useParams();
  const {
    refetch,
    data: bookingData,
    isError: getBookingError,
    isLoading: isBookingLoading,
    isSuccess: bookingSuccess,
  } = useGetBookingDetails(bookingId);

  const formatTime = useCallback((seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs
      .toString()
      .padStart(2, '0')}`;
  }, []);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startTimer = useCallback(
    (seconds: number = 180) => {
      clearTimer();
      setTimeLeft(seconds);

      const id = window.setInterval(async () => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearTimer();
            navigate(
              `/confirmed-booking/${bookingId}/true?locationCode=${locationCode}`,
              { replace: true }
            );
            return 0;
          }

          // Every 60 seconds, refetch and check payment
          if ((prev - 1) % 60 === 0) {
            refetch();
            if (bookingData?.OnlinePaymentStatus === 2) {
              clearTimer();
              navigate(
                `/confirmed-booking/${bookingId}/true?locationCode=${locationCode}`,
                { replace: true }
              );
            }
          }

          return prev - 1;
        });
      }, 1000);

      timerRef.current = id;
    },
    [bookingId, bookingData, clearTimer, navigate, refetch, locationCode]
  );

  useEffect(() => {
    if (!bookingId) return;
    startTimer();
    return () => clearTimer(); // cleanup on unmount
  }, [bookingId, startTimer, clearTimer]);

  return (
    <Card className="pay-amount-card">
      <Box className="qr-code">
        <QRCodeSVG
          value={
            bookingSearchData.paymentUrl ? bookingSearchData.paymentUrl : '/'
          }
          width={226}
          height={120}
          level="H"
          marginSize={2}
        />
      </Box>
      <CardContent>
        <Typography variant="h6" sx={{ marginBottom: '10px' }}>
          Scan QR Code to pay
        </Typography>
        <Typography className="p">
          Scan QR code and make payment using Credit/Debit cards, Apple Pay,
          Samsung Pay, etc.
        </Typography>
        <Divider
          sx={{
            backgroundColor: 'lightgray',
            height: '1px',
            margin: '20px 0px',
          }}
        />
        <Typography variant="h6">Total Amount to pay</Typography>
        <Typography className="p">
          AED <strong>{price}</strong> / {days} Days
        </Typography>
        <Divider
          sx={{
            backgroundColor: 'lightgray',
            height: '1px',
            margin: '20px 0px',
          }}
        />
        <Typography className="p" sx={{ marginBottom: '20px' }}>
          Payment link will expire with in{' '}
          <strong style={{ textDecoration: 'underline' }}>
            {formatTime(timeLeft)} minutes.
          </strong>{' '}
          waiting for payment completion
        </Typography>
        <LoaderV1 />
      </CardContent>
    </Card>
  );
};
function setIsError(arg0: Error) {
  throw new Error('Function not implemented.');
}

function setIsLoading(arg0: boolean) {
  throw new Error('Function not implemented.');
}
